import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  Snackbar, Alert
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import Layout from '../components/Layout/Layout';
import UserForm from '../components/Users/UserForm';
import axios from '../api/axiosConfig';

const fetchUsers = async () => {
  const { data } = await axios.get('/users');
  return data;
};

const deleteUser = async (id) => {
  await axios.delete(`/users/${id}`);
};

const UsersPage = () => {
  const [openForm, setOpenForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();
  const { data: users, isLoading } = useQuery({ queryKey: ['users'], queryFn: fetchUsers });
  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries(['users']);
      setSnackbar({ open: true, message: 'User deleted', severity: 'success' });
      setDeleteConfirm(null);
    },
    onError: (err) => setSnackbar({ open: true, message: err.response?.data?.message || 'Error', severity: 'error' })
  });

  const handleEdit = (user) => {
    setEditingUser(user);
    setOpenForm(true);
  };

  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  if (isLoading) return <Layout><Typography>Loading...</Typography></Layout>;

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>Users Management</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEditingUser(null); setOpenForm(true); }} sx={{ mb: 2 }}>
          Add User
        </Button>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell><TableCell>Email</TableCell><TableCell>Full Name</TableCell><TableCell>Role</TableCell><TableCell>Phone</TableCell><TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users?.map(user => (
                <TableRow key={user._id}>
                  <TableCell>{user.username}</TableCell><TableCell>{user.email}</TableCell><TableCell>{user.fullName}</TableCell><TableCell>{user.role}</TableCell><TableCell>{user.phone}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleEdit(user)}><Edit /></IconButton>
                    <IconButton onClick={() => setDeleteConfirm(user._id)}><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent><DialogContentText>Are you sure?</DialogContentText></DialogContent>
          <DialogActions><Button onClick={() => setDeleteConfirm(null)}>Cancel</Button><Button onClick={() => handleDelete(deleteConfirm)} color="error">Delete</Button></DialogActions>
        </Dialog>

        <UserForm open={openForm} onClose={() => { setOpenForm(false); setEditingUser(null); }} user={editingUser} onSuccess={() => { queryClient.invalidateQueries(['users']); setSnackbar({ open: true, message: 'User saved', severity: 'success' }); }} />

        <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default UsersPage;